import React, { useState, useEffect } from "react";
import Input from "../input";
import { Controller, useForm } from "react-hook-form";
import FIlterSelect from "../filter-select";
import { Modal } from "antd";
import StepIndicator from "./indicator";
import CustomerDetails from "./customer-details";
import Kyc from "./kyc";
import Button from "../button";
import Icon from "../../assets/icons";
import { Plus } from "lucide-react";
import { showToast } from "../toast";
import { RingLoader } from "react-spinners";
import useApi from "../hooks/request";

interface AddCustomerDialogProps {
  isVisible: boolean;
  onClose: () => void;
  customer?: any;
}

type FormStep = "customerDetails" | "kyc" | "paymentMethod";
type ValidationField = string;

const AddCustomerDialog: React.FC<AddCustomerDialogProps> = ({
  isVisible,
  onClose,
  customer,
}) => {
  const [accountType, setAccountType] = useState("b2c");
  const [currentStep, setCurrentStep] = useState<FormStep>("customerDetails");
  const {
    control,
    setValue,
    handleSubmit,
    trigger,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onChange", // This will make validation run on change
  });
  const { loading, makeRequest } = useApi("/customers", "POST");

  // Watch for changes in key form fields to clear errors automatically
  const watchedFields = watch();
  const creditType = watch("creditType"); // Watch creditType field

  // Effect to clear errors when fields are filled
  useEffect(() => {
    const checkAndClearErrors = async () => {
      let fieldsToValidate: ValidationField[] = [];

      // Define fields to check based on current step
      if (currentStep === "customerDetails") {
        if (accountType === "b2c") {
          fieldsToValidate = ["email", "contactName", "contactNumber"];
        } else {
          fieldsToValidate = [
            "email",
            "contactName",
            "contactNumber",
            "businessName",
            "businessType",
          ];
        }
      } else if (currentStep === "kyc") {
        fieldsToValidate = [
          "businessRegistrationNumber",
          "street",
          "city",
          "state",
        ];
      } else if (currentStep === "paymentMethod") {
        fieldsToValidate = ["paymentPeriod", "creditLimit"];
      }

      // Check each field and clear its error if it has a value
      fieldsToValidate.forEach((field) => {
        if (watchedFields[field]) {
          clearErrors(field);
        }
      });
    };

    checkAndClearErrors();
  }, [watchedFields, currentStep, accountType, clearErrors]);

  const handleCreateCustomer = handleSubmit(async (formData) => {
    const form = new FormData();

    // Common fields for both b2c and b2b
    form.append("email", formData.email);
    form.append("accountType", accountType);
    form.append("contactName", formData.contactName);
    form.append("contactNumber", formData.contactNumber);
    form.append("address.street", formData.street || "");
    form.append("address.city", formData.city || "");
    form.append("address.state", formData.state || "");

    if (accountType === "b2b") {
      form.append("businessName", formData.businessName);
      form.append("businessType", formData.businessType);
      form.append("creditStatus.type", formData.creditType);

      if (formData.businessCertificate) {
        form.append("businessCertificate", formData.businessCertificate);
      }
      if (formData.businessOwnerId) {
        form.append("businessOwnerId", formData.businessOwnerId);
      }
    }

    const [response] = await makeRequest(form);
    if (response.status) {
      showToast(response.message, true, { position: "top-right" });
      onClose();
      window.location.reload();
    } else {
      showToast(response?.message, false, {
        position: "top-right",
      });
    }
  });

  const validateAndProceed = async () => {
    let fieldsToValidate: ValidationField[] = [];

    if (currentStep === "customerDetails") {
      if (accountType === "b2c") {
        fieldsToValidate = [
          "email",
          "contactName",
          "contactNumber",
          "street",
          "city",
          "state",
        ];
      } else {
        fieldsToValidate = [
          "email",
          "contactName",
          "contactNumber",
          "businessName",
          "businessType",
          "creditType"
        ];
      }
    } else if (currentStep === "kyc") {
      fieldsToValidate = [
        "businessRegistrationNumber",
        "street",
        "city",
        "state",
        "businessCertificate",
        "businessOwnerId",
      ];
    }

    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      if (currentStep === "customerDetails") {
        // If credit type is not "credit", create customer directly
        if (accountType === "b2b" && creditType !== "credit") {
          handleCreateCustomer();
        } else if (accountType === "b2b" && creditType === "credit") {
          setCurrentStep("kyc");
        } else {
          setCurrentStep("paymentMethod");
        }
      } else if (currentStep === "kyc") {
        setCurrentStep("paymentMethod");
      }
    }
  };

  const goBackToPreviousStep = () => {
    if (currentStep === "kyc") {
      setCurrentStep("customerDetails");
    } else if (currentStep === "paymentMethod") {
      // If KYC was skipped, go back to customer details
      if (accountType === "b2b" && creditType === "credit") {
        setCurrentStep("kyc");
      } else {
        setCurrentStep("customerDetails");
      }
    }
  };

  // Render Payment Method step
  const renderPaymentMethodStep = () => {
    return (
      <>
        <div className="items-center gap-4 mt-4">
          <Controller
            name="paymentPeriod"
            control={control}
            defaultValue={customer?.contactName}
            rules={{
              required: "Payment Period is required",
              validate: {
                onlyNumbers: (value) =>
                  /^\d+$/.test(value) ||
                  "Payment Period must contain only numbers",
              },
            }}
            render={({ field, fieldState }) => (
              <Input
                value={field.value}
                className="w-full"
                label="Payment Period"
                placeholder="Enter Payment Period"
                error={fieldState?.error?.message}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  field.onChange(value);
                }}
                autoComplete="off"
              />
            )}
          />
        </div>

        <div className="w-full">
          <div className="text-sm mb-1">Credit Limit</div>
          <div className="flex w-full border border-[#9D9898] rounded-full overflow-hidden">
            <div className="bg-[#F5F5F5] border-r border-[#9D9898] px-3 py-2 flex items-center justify-center">
              ₦
            </div>
            <Controller
              name="creditLimit"
              control={control}
              defaultValue={customer?.creditLimit}
              rules={{
                required: "Credit Limit is required",
              }}
              render={({ field, fieldState }) => (
                <input
                  value={field.value}
                  className="rounded-l-none w-full outline-none px-3"
                  placeholder="Amount Text"
                  onChange={(e) => {
                    field.onChange(e);
                    if (e.target.value) clearErrors("creditLimit");
                  }}
                  autoComplete="off"
                />
              )}
            />
            <div className="bg-[#F5F5F5] border-l border-[#9D9898] px-3 py-2 flex items-center justify-center cursor-pointer">
              <span className="mr-1">NGN</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 9L12 15L18 9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "customerDetails":
        return (
          <CustomerDetails
            accountType={accountType}
            setAccountType={setAccountType}
            control={control}
            clearErrors={clearErrors}
            errors={errors}
            customer={customer}
          />
        );
      case "kyc":
        return (
          <Kyc
            control={control}
            setValue={setValue}
            clearErrors={clearErrors}
            errors={errors}
            customer={customer}
          />
        );
      case "paymentMethod":
        return renderPaymentMethodStep();
      default:
        return (
          <CustomerDetails
            accountType={accountType}
            setAccountType={setAccountType}
            control={control}
            clearErrors={clearErrors}
            errors={errors}
            customer={customer}
          />
        );
    }
  };

  // Render the step-specific footer buttons
  const renderFooterButtons = () => {
    if (accountType === "b2c") {
      return (
        <div className="flex justify-end gap-3 py-2 mt-4">
          <Button
            type="submit"
            size="lg"
            variant="primary"
            className="hover:bg-veggie-700"
          >
            {loading ? (
              <RingLoader color="#ffffff" loading={loading} size={20} />
            ) : (
              <div className="flex gap-3">
                Add Customer <Plus />
              </div>
            )}
          </Button>
        </div>
      );
    }

    if (currentStep === "customerDetails") {
      return (
        <div className="flex gap-3 py-2 mt-4">
          <Button
            type="button"
            className="hover:bg-green-700 flex items-center"
            onClick={validateAndProceed}
            variant="primary"
            size="lg"
          >
            <Icon name="noteIcon" />
            {creditType === "credit" ? "Proceed to KYC" : "Create Customer"}
          </Button>
        </div>
      );
    } else if (currentStep === "kyc") {
      return (
        <div className="flex gap-2 items-center text-center py-2 mt-4">
          <button
            type="button"
            className="bg-[#E8F2E8] w-full rounded-full hover:bg-gray-200 px-7 py-2 text-[#047401] flex font-medium"
            onClick={goBackToPreviousStep}
          >
            <Icon name="backIcon" />
            Back to Customer Details
          </button>
          <Button
            type="button"
            size="lg"
            variant="primary"
            className="hover:bg-green-700 text-white flex"
            onClick={validateAndProceed}
          >
            <Icon name="noteIcon" />
            Proceed to Credit Status
          </Button>
        </div>
      );
    } else if (currentStep === "paymentMethod") {
      const backButtonText = creditType === "credit" ? "Back to KYC" : "Back to Customer Details";
      
      return (
        <div className="flex justify-between gap-2 py-2 mt-4">
          <button
            type="button"
            className="bg-[#E8F2E8] w-full rounded-full hover:bg-gray-200 pl-20 py-2 text-[#047401] flex font-medium"
            onClick={goBackToPreviousStep}
          >
            <Icon name="backIcon" />
            {backButtonText}
          </button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="hover:bg-green-700 text-white flex items-center"
          >
            {loading ? (
              <RingLoader color="#ffffff" loading={loading} size={20} />
            ) : (
              <div className="flex gap-3">
                Submit for Approval <Icon name="noteIcon" />
              </div>
            )}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      visible={isVisible}
      onCancel={onClose}
       width="80%"
        centered={true}
        footer={null}
        style={{
          padding: 0,
          margin: 0,
          maxWidth: "1200px",
          height: "auto",
        }}
    >
      <h1 className="text-2xl font-bold -mt-2 mb-4">Add Customer</h1>

      {creditType === "credit" && (
        <StepIndicator
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      )}

      <form onSubmit={handleCreateCustomer}>
        <div className="grid gap-4">{renderStepContent()}</div>
        {renderFooterButtons()}
      </form>
    </Modal>
  );
};

export default AddCustomerDialog;